/**
 * UPMI — bespoke PROMAG300 storefront (Layer-3 tenant override of
 * `@app/storefront/tenant-landing`). Faithful recreation of the design handoff.
 *
 * This file is the SEAM home.tsx consumes:
 *   - HAS_BESPOKE_LANDING   — turn the override on
 *   - TenantLanding()       — the Home landing (served at homePath '/')
 *   - renderTenantPage(path)— bespoke Gallery/About/Contact for their layout
 *                             pages (auto-mounted by App.tsx), else null
 *
 * All pages share <UpmiShell/> (utility bar + teal header + nav + footer).
 * BLESSED folder use — no business logic; products/tiers read from the CMS.
 */
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Plus, Volume2, VolumeX } from 'lucide-react';
import {
  useBrand,
  useLandingPackages,
  useFeaturedPackages,
  useProductHref,
  displaySellerName,
} from '../../../storefront/lib/hooks';
import { useCartStore } from '../../../storefront/lib/cart-store';
import { UpmiShell } from './shell';
import { UpmiAbout } from './about';
import { UpmiGallery } from './gallery';
import { UpmiContact } from './contact';
import { UpmiProducts } from './marketplace';
import {
  CARD,
  GR_BD,
  GR_INK,
  GR_L,
  INK,
  LINE,
  MEDIA,
  MUTED,
  O,
  OD,
  OGRAD,
  OL,
  VIDEO_BG,
  fmtPrice,
} from './tokens';

export const HAS_BESPOKE_LANDING = true;

export function TenantLanding(): JSX.Element {
  return (
    <UpmiShell active="home">
      <UpmiHome />
    </UpmiShell>
  );
}

/** Bespoke pages for non-home paths. Routed via TENANT_ROUTES (build-time), so
 * they work regardless of the tenant's stored layout. */
export function renderTenantPage(path: string): JSX.Element | null {
  if (path === '/storefront') return <UpmiProducts />;
  if (path === '/gallery') return <UpmiGallery />;
  if (path === '/about-us') return <UpmiAbout />;
  if (path === '/contact') return <UpmiContact />;
  return null;
}
export const TENANT_ROUTES = ['/gallery', '/about-us', '/contact'];

function UpmiHome(): JSX.Element {
  const brand = useBrand();
  // Both product rows are admin-curated & purchasable (like mhyel): Featured
  // Products ← Admin → Featured Packages; Membership Packages ← Admin → Landing
  // Packages. An empty list hides its section until the admin curates it.
  const hrefFor = useProductHref();
  const featured = useFeaturedPackages().products.slice(0, 5);
  const packages = useLandingPackages().products;
  const navigate = useNavigate();
  const addToCart = useCartStore((s) => s.add);
  const [addedId, setAddedId] = useState<string | null>(null);
  // Background hero video. The element ships MUTED so the browser's own autoplay
  // policy is satisfied and playback starts without any JS at all; sound (which
  // the brand asked for) is then attempted as a pure enhancement once metadata
  // lands. It used to render unmuted and depend entirely on `onLoadedMetadata`
  // firing to recover — so anywhere that event was late or never fired (iOS
  // Safari and Android data-saver routinely skip preloading on cellular) the
  // video simply never played.
  const [heroMuted, setHeroMuted] = useState(true);
  // Set when the video source itself fails; the poster stays visible instead of
  // an empty box, so the hero is never blank.
  const [heroVideoFailed, setHeroVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Play the brand video slightly slowed for a calmer ambient feel. Tweak/remove.
  const HERO_VIDEO_SPEED = 0.75;

  function cartAdd(p: (typeof featured)[number]) {
    addToCart({
      productId: p.id,
      name: p.name,
      price: p.price,
      was: p.was,
      image: p.image,
      variation: '',
      pv: p.pv,
      sellerId: p.sellerId,
      sellerName: displaySellerName(p.sellerId, p.sellerName, brand),
      qty: 1,
    });
  }

  return (
    <>
      {/* Immersive hero — full-bleed brand video background with copy overlaid.
          The copy is SPLIT to the top and bottom edges (`column` +
          `space-between`) so the middle band stays clear, because the film burns
          in its own PROMAG300 PREMIER wordmark and the client reported the CTAs
          covering it.

          Measured off the clip rather than guessed — the wordmark is ANIMATED
          and moves: ~38-51% at 4s, ~44-62% at 11s, dropping to ~64-84% over the
          logo frames at 0s/2s/22s/24s. Union = roughly 38%-84% of frame height,
          so the only reliably text-free bands are the top ~28% and the bottom
          ~12%, which is what the padding below targets. (A previous comment here
          claimed the wordmark sat "in the lower half" and put all the copy in the
          upper third; that was wrong for most of the clip and is what produced
          the overlap.) `objectFit: cover` means these are frame-relative, and at
          typical desktop aspect ratios the full frame height is visible, so they
          map straight onto the section.

          The poster doubles as the section background, so the hero paints a real
          frame on the very first render — while the video buffers, and still if
          it fails outright. The teal sits behind it as the last resort. */}
      <section
        style={{
          position: 'relative',
          minHeight: 'clamp(520px,72vh,640px)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `${VIDEO_BG} url("${MEDIA}/homebanner-poster.jpg") center / cover no-repeat`,
          color: '#fff',
          textAlign: 'center',
          padding: 'clamp(32px,4.5vh,42px) 24px clamp(22px,3vh,28px)',
          overflow: 'hidden',
        }}
      >
        {/* Full-bleed background video. `muted` + `playsInline` are what actually
            satisfy the iOS/Android autoplay policy, so they are on the element
            itself rather than applied later from JS; `loop` keeps it running. */}
        <video
          ref={videoRef}
          src={`${MEDIA}/homebannervideo.mp4`}
          poster={`${MEDIA}/homebanner-poster.jpg`}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          aria-hidden
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            v.playbackRate = HERO_VIDEO_SPEED;
            // Sound is an enhancement on top of playback that is already running
            // muted: try unmuting, and put it straight back if the autoplay
            // policy refuses, so a refusal can never leave the hero stopped.
            v.muted = false;
            void Promise.resolve(v.play())
              .then(() => setHeroMuted(false))
              .catch(() => {
                v.muted = true;
                setHeroMuted(true);
                void v.play();
              });
          }}
          onError={() => setHeroVideoFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            display: heroVideoFailed ? 'none' : 'block',
          }}
        />
        {/* No wash over the video — the brand wants the raw footage. Legibility
            comes from the headline's own text-shadow instead of a full-bleed
            overlay, so the video's colour reads as shot. */}
        {/* Headline — TOP band. No eyebrow: the video already burns in the
            PROMAG300 PREMIER wordmark, so an overlaid one just said it twice.
            `maxWidth` is 34ch, not 20ch: at 20ch each sentence wrapped onto two
            lines, making a four-line block deep enough to reach the wordmark. */}
        <h1
          style={{
            position: 'relative',
            zIndex: 2,
            fontSize: 'clamp(26px,3.4vw,40px)',
            lineHeight: 1.16,
            fontWeight: 800,
            letterSpacing: '-.02em',
            margin: 0,
            maxWidth: '34ch',
            textShadow: '0 2px 10px rgba(0,0,0,.45), 0 1px 32px rgba(0,0,0,.35)',
          }}
        >
          Discover the power of magnesium.
          <br />
          And experience the difference.
        </h1>
        {/* CTAs — BOTTOM band, a sibling of the headline rather than nested under
            it, so `space-between` can pin the two to opposite edges. Sized with
            clamps so the pair stays on ONE row as far down as it can: a wrapped
            second row grows upward into the wordmark. */}
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            gap: 14,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Link
            to="/storefront"
            style={{
              background: '#fff',
              color: O,
              fontWeight: 800,
              fontSize: 'clamp(12px,1.35vw,14px)',
              padding: 'clamp(9px,1.2vw,13px) clamp(14px,2.6vw,30px)',
              borderRadius: 999,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            Shop PROMAG300 <ArrowRight size={17} />
          </Link>
          <Link
            to="/about-us"
            style={{
              background: 'transparent',
              color: '#fff',
              fontWeight: 700,
              fontSize: 'clamp(12px,1.35vw,14px)',
              border: '1.5px solid #fff',
              padding: 'clamp(7.5px,1.2vw,11.5px) clamp(14px,2.6vw,30px)',
              borderRadius: 999,
              textDecoration: 'none',
            }}
          >
            Become a Member
          </Link>
        </div>
        {/* Mute / unmute toggle for the background video. Hidden when there is no
            video to toggle. It sits ABOVE the CTA band rather than in the bottom
            corner: the CTAs are centred and the toggle is right-aligned, and
            between roughly 420-460px wide the row grows just far enough to reach
            it. Offsetting by the bottom padding + the row's height keeps them
            apart at every width instead of only most of them. */}
        {!heroVideoFailed && (
          <button
            type="button"
            onClick={() => {
              const v = videoRef.current;
              if (!v) return;
              v.muted = !v.muted;
              setHeroMuted(v.muted);
              if (!v.muted) void v.play();
            }}
            aria-label={heroMuted ? 'Unmute background video' : 'Mute background video'}
            style={{
              position: 'absolute',
              zIndex: 3,
              right: 16,
              bottom: 'calc(clamp(22px,3vh,28px) + 58px)',
              width: 42,
              height: 42,
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,.35)',
              background: 'rgba(10,68,68,.45)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backdropFilter: 'blur(4px)',
            }}
          >
            {heroMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
        )}
      </section>

      {/* Featured Products */}
      {featured.length > 0 && (
        <section style={{ maxWidth: 1180, margin: '0 auto', padding: '56px 16px 8px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 16,
              marginBottom: 22,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '.18em',
                  textTransform: 'uppercase',
                  color: O,
                }}
              >
                Shop the range
              </div>
              <h2 style={{ fontSize: 26, fontWeight: 800, margin: '6px 0 0', color: INK }}>
                Featured Products
              </h2>
            </div>
            <Link
              to="/storefront"
              style={{ color: O, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}
            >
              View all products ›
            </Link>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))',
              gap: 12,
            }}
          >
            {featured.map((p) => (
              <div
                key={p.id}
                style={{
                  background: CARD,
                  border: `1px solid ${LINE}`,
                  borderRadius: 8,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Link
                  to={hrefFor(p)}
                  style={{ aspectRatio: '1', background: OL, display: 'block', overflow: 'hidden' }}
                >
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : null}
                </Link>
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <Link to={hrefFor(p)} style={{ textDecoration: 'none', color: INK }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        lineHeight: 1.32,
                        height: 33,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {p.name}
                    </div>
                  </Link>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
                    <span style={{ color: O, fontWeight: 800, fontSize: 16.5 }}>
                      {fmtPrice(p.price)}
                    </span>
                    {p.was && p.was > p.price ? (
                      <span style={{ color: '#aaa', fontSize: 12, textDecoration: 'line-through' }}>
                        {fmtPrice(p.was)}
                      </span>
                    ) : null}
                  </div>
                  {p.pv ? (
                    <span
                      style={{
                        marginTop: 8,
                        alignSelf: 'flex-start',
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: GR_INK,
                        background: GR_L,
                        border: `1px solid ${GR_BD}`,
                        borderRadius: 4,
                        padding: '2px 6px',
                      }}
                    >
                      {p.pv}
                    </span>
                  ) : null}
                  <button
                    onClick={() => {
                      cartAdd(p);
                      setAddedId(p.id);
                    }}
                    style={{
                      marginTop: 10,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      background: addedId === p.id ? OD : O,
                      color: '#fff',
                      border: 0,
                      borderRadius: 6,
                      padding: '8px 10px',
                      fontWeight: 700,
                      fontSize: 12.5,
                    }}
                  >
                    {addedId === p.id ? (
                      <>
                        Added <Check size={14} />
                      </>
                    ) : (
                      <>
                        Add <Plus size={14} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Membership Packages — real, admin-curated products (Admin → Landing Packages) */}
      {packages.length > 0 && (
        <section style={{ maxWidth: 1180, margin: '0 auto', padding: '56px 16px' }}>
          <div style={{ textAlign: 'center', marginBottom: 34 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: '.18em',
                textTransform: 'uppercase',
                color: O,
              }}
            >
              Start your business
            </div>
            <h2 style={{ fontSize: 26, fontWeight: 800, margin: '6px 0 8px', color: INK }}>
              Membership Packages
            </h2>
            <p style={{ color: MUTED, fontSize: 14.5, margin: 0 }}>
              Choose the package that matches your goals — pricing and rewards scale with your
              commitment.
            </p>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
              gap: 20,
              maxWidth: 1040,
              margin: '0 auto',
            }}
          >
            {packages.map((p) => (
              <div
                key={p.id}
                style={{
                  background: CARD,
                  border: `1px solid ${LINE}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 2px 12px rgba(0,0,0,.05)',
                }}
              >
                <Link
                  to={hrefFor(p)}
                  style={{ aspectRatio: '1', background: OL, display: 'block', overflow: 'hidden' }}
                >
                  {p.image ? (
                    <img
                      src={p.image}
                      alt={p.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : null}
                </Link>
                <div
                  style={{
                    padding: '16px 16px 18px',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                  }}
                >
                  <Link to={hrefFor(p)} style={{ textDecoration: 'none', color: INK }}>
                    <h3 style={{ fontSize: 15.5, fontWeight: 700, lineHeight: 1.35, margin: 0 }}>
                      {p.name}
                    </h3>
                  </Link>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 8,
                      marginTop: 10,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ color: O, fontWeight: 800, fontSize: 22, lineHeight: 1 }}>
                      {fmtPrice(p.price)}
                    </span>
                    {p.was && p.was > p.price ? (
                      <span style={{ color: '#aaa', fontSize: 13, textDecoration: 'line-through' }}>
                        {fmtPrice(p.was)}
                      </span>
                    ) : null}
                    {p.pv ? (
                      <span
                        style={{
                          fontSize: 10.5,
                          fontWeight: 700,
                          color: GR_INK,
                          background: GR_L,
                          border: `1px solid ${GR_BD}`,
                          borderRadius: 4,
                          padding: '2px 6px',
                        }}
                      >
                        {p.pv}
                      </span>
                    ) : null}
                  </div>
                  <div style={{ flex: 1 }} />
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button
                      onClick={() => {
                        cartAdd(p);
                        setAddedId(p.id);
                      }}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        background: '#fff',
                        color: O,
                        fontWeight: 700,
                        fontSize: 13,
                        padding: '11px 12px',
                        borderRadius: 8,
                        border: `1.5px solid ${O}`,
                      }}
                    >
                      {addedId === p.id ? 'Added ✓' : 'Add to Cart'}
                    </button>
                    <button
                      onClick={() => {
                        cartAdd(p);
                        navigate('/checkout');
                      }}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        background: O,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 13,
                        padding: '11px 12px',
                        borderRadius: 8,
                        border: 0,
                      }}
                    >
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sign-up banner */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '8px 16px 64px' }}>
        <div
          style={{
            background: OGRAD,
            borderRadius: 10,
            padding: 40,
            textAlign: 'center',
            color: '#fff',
          }}
        >
          <h2
            style={{
              fontSize: 24,
              fontWeight: 800,
              margin: '0 auto 20px',
              maxWidth: '30ch',
              lineHeight: 1.35,
            }}
          >
            Join Ultra Proactive today and enjoy exclusive discounts, special perks, and
            members-only offers.
          </h2>
          <button
            onClick={() => navigate('/signup')}
            style={{
              cursor: 'pointer',
              background: '#fff',
              color: O,
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: '.02em',
              padding: '13px 32px',
              borderRadius: 999,
              border: 0,
            }}
          >
            SIGN UP NOW
          </button>
        </div>
      </section>
    </>
  );
}
